<script setup lang="ts">
import type { DownloadImageDto } from "core/dto";

import { getImageUrl } from "~/common/helpers/get-image-url";
import { TAG } from "~/common/interfaces/tag.enum";

const props = defineProps({
	imageIds: {
		type: Array as PropType<number[]>,
		default () {
			return [];
		}
	},
	imageSize: {
		type: Object as PropType<Required<DownloadImageDto>>,
		default () {
			return {
				width: 480,
				height: 640
			};
		}
	},
	tags: {
		type: Array as PropType<string[]>,
		default () {
			return [];
		}
	},
	showThumbnails: {
		type: Boolean,
		default: false
	}
});
defineEmits(["click:nested"]);

const swiper = ref();
const swiperThumbs = ref();
const isHovered = ref(false);

const modules = computed(() => {
	const modules = [SwiperA11y];
	if (props.showThumbnails) {
		modules.push(SwiperThumbs, SwiperFreeMode);
	}

	return modules;
});

const getTagClasses = (tag: string) => tag.toLowerCase() === TAG.NEW ? "left-3" : "right-3";

const thumbnailImageSize = computed(() => {
	return {
		height: Math.round(props.imageSize.height * 0.2375),
		width: Math.round(props.imageSize.width * 0.2375)
	};
});
</script>

<template>
	<Swiper
		:lazy-preload-prev-next="1"
		:modules="modules"
		:slides-per-view="1"
		:space-between="32"
		:thumbs="{swiper: swiperThumbs}"
		lazy
		loop
		@mouseleave="isHovered = false"
		@mouseover="isHovered = true"
		@swiper="(s) => swiper = s"
	>
		<SwiperSlide v-for="(id, i) in imageIds" :key="id">
			<figure>
				<NuxtImg
					class="bg-gray"
					:src="getImageUrl(id, { width: imageSize.width * 2, height: imageSize.height * 2 })"
					:loading="i ? 'lazy' : undefined"
					:width="imageSize.width"
					:height="imageSize.height"
					alt="image"
					@click="$emit('click:nested')"
				/>
			</figure>
		</SwiperSlide>
		<div
			v-for="tag in tags"
			:key="tag"
			class="absolute z-10 top-3 text-sm text-white h-8 w-8 rounded-full flex justify-center items-center bg-black uppercase"
			:class="getTagClasses(tag)"
		>
			<span class="text-sm font-medium">{{ tag }}</span>
		</div>
		<div v-show="isHovered && imageIds.length > 1">
			<AppButtonIcon
				class="absolute z-10 top-1/2 -translate-y-1/2 left-2 text-black/40 hover:text-black duration-100"
				name="teenyicons:left-solid"
				size="20"
				@click="swiper.slidePrev()"
			/>
			<AppButtonIcon
				class="absolute z-10 top-1/2 -translate-y-1/2 right-2 text-black/40 hover:text-black duration-100"
				name="teenyicons:right-solid"
				size="20"
				@click="swiper.slideNext()"
			/>
		</div>
	</Swiper>
	<Swiper
		v-if="showThumbnails"
		class="mt-2"
		:modules="modules"
		:height="thumbnailImageSize.height"
		:slides-per-view="4"
		:space-between="8"
		free-mode
		watch-slides-progress
		@swiper="(s) => swiperThumbs = s"
	>
		<SwiperSlide
			v-for="id in imageIds"
			:key="id"
			class="cursor-pointer"
		>
			<NuxtImg
				:src="getImageUrl(id, { width: thumbnailImageSize.width * 2, height: thumbnailImageSize.height * 2 })"
				:height="thumbnailImageSize.height"
				:width="thumbnailImageSize.width"
				alt="thumbnail-image"
			/>
		</SwiperSlide>
	</Swiper>
</template>
